import { getFinanceLabMetrics } from "./FinanceLab.queries";
import { FinanceLabField, ResampleTransform } from "./FinanceLab.model";
import { useApiQuery } from "@/api";
import { PeriodKind } from "@/entities/Labeling/Account.model";
import { getLastValue } from "@/utils/metrics/metricsUtils";

export function useFinanceLabMetrics() {
    const metrics = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
            ],
            periodKind: PeriodKind.MONTHLY,
        },
    });

    const ltmMetrics = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
                FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
            ],
            resampleTransform: ResampleTransform.LAST_12_MONTHS,
        },
    });

    const revenueValues = ltmMetrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_REVENUE,
    )?.values;

    const ebitdaValues = ltmMetrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_EBITDA,
    )?.values;

    const salesAndMarketingEfficiencyValues = ltmMetrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_SALES_AND_MARKETING_EFFICIENCY,
    )?.values;

    const cashValues = metrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
    )?.values;

    const totalAssetsValues = metrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
    )?.values;

    const totalLiabilitiesValues = metrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
    )?.values;

    const netIncomeValues = ltmMetrics.data?.find(
        (metric) =>
            metric.info.derived.label ===
            FinanceLabField.ORGANIZATION_FINANCIAL_DATA_NET_INCOME,
    )?.values;

    return {
        revenue: getLastValue(revenueValues)?.formattedValue || "-",
        ebitda: getLastValue(ebitdaValues)?.formattedValue || "-",
        cash: getLastValue(cashValues)?.formattedValue || "-",
        totalAssets: getLastValue(totalAssetsValues)?.formattedValue || "-",
        totalLiabilities:
            getLastValue(totalLiabilitiesValues)?.formattedValue || "-",
        netIncome: getLastValue(netIncomeValues)?.formattedValue || "-",
        salesAndMarketingEfficiency:
            getLastValue(salesAndMarketingEfficiencyValues)?.formattedValue ||
            "-",
        revenueValues,
        ebitdaValues,
        cashValues,
        totalAssetsValues,
        totalLiabilitiesValues,
        netIncomeValues,
        metrics: [...(metrics.data ?? []), ...(ltmMetrics.data ?? [])],
        isLoading: metrics.isLoading || ltmMetrics.isLoading,
        isError: metrics.isError || ltmMetrics.isError,
    };
}
